// Currency utils --------------------------------------------------------
import { APP_LANGUAGE, DEFAULT_CURRENCY_CODE } from '@egw/constants'

// Function type for translation function that accepts any key
type UtilsTranslationFunction = (_key: string, _options?: Record<string, unknown>) => string

interface Money {
  amount: number
  currencyCode: string
}

/**
 * Translation keys for currency utilities
 */
export const CURRENCY_TRANSLATION_KEYS = {
  SYMBOLS: {
    USD: 'currency.symbols.USD',
  },
  GENERAL: {
    PERCENT_FORMATTER: 'general.percent_formatter',
  },
} as const

const currencySymbols: Record<string, string> = {
  USD: '$',
}

export function getCurrencySymbol(currencyCode: string): string {
  return currencySymbols[currencyCode] || currencySymbols[DEFAULT_CURRENCY_CODE]
}

export function removeCurrencySymbols(valueWithSymbol: string): number {
  return parseFloat(valueWithSymbol.replace(getCurrencySymbol(DEFAULT_CURRENCY_CODE), '').split(',').join(''))
}

const currencyFormatter = new Intl.NumberFormat(APP_LANGUAGE, {
  style: 'currency',
  currency: 'USD',
})

export enum ECurrencyFormatSign {
  PlusMinus = 'plusMinus',
  OnlyMinus = 'onlyMinus',
  OmitSign = 'omitSign',
}

export enum ECurrencyFormatCents {
  IntegerCents = 'integerCents',
  OnlyInteger = 'onlyInteger',
  OnlyCents = 'onlyCents',
}

export const formatCurrency = ({
  amount,
  sign = ECurrencyFormatSign.OnlyMinus,
  cents = ECurrencyFormatCents.IntegerCents,
}: {
  amount?: number
  sign?: ECurrencyFormatSign
  cents?: ECurrencyFormatCents
}): string => {
  if (amount === undefined || isNaN(amount)) return ''

  let parts = currencyFormatter
    .formatToParts(amount)
    // removing currency symbol
    .filter((item) => item.type !== 'currency')

  if (cents === ECurrencyFormatCents.OnlyInteger) {
    // removing cents, leaving integers
    parts = parts.filter((item) => item.type !== 'decimal').filter((item) => item.type !== 'fraction')
  } else if (cents === ECurrencyFormatCents.OnlyCents) {
    // removing integers, leaving cents
    parts = parts.filter((item) => item.type === 'decimal' || item.type === 'fraction')
  }

  if (sign === ECurrencyFormatSign.OmitSign) {
    // removing minus symbol from negative values and returning
    parts = parts.filter((item) => item.type !== 'minusSign')

    return parts.map((item) => item.value).join('')
  }

  if (sign === ECurrencyFormatSign.PlusMinus && amount > 0) {
    // adding plus symbol to positive values and returning
    return `+${parts.map((item) => item.value).join('')}`
  }

  // replace minus sign with en dash
  return parts.map((item) => (item.value === '-' ? '–' : item.value)).join('')
}

// Currency formatting utility functions
export const Currency: {
  integerCents: {
    onlyMinus: (amount: number) => string
    plusMinus: (amount: number) => string
    omitSign: (amount: number) => string
  }
  onlyInteger: {
    onlyMinus: (amount: number) => string
    plusMinus: (amount: number) => string
    omitSign: (amount: number) => string
  }
  onlyCents: {
    omitSign: (amount: number) => string
  }
} = {
  integerCents: {
    // example positive: 1000.67   negative: -1000.67
    onlyMinus: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.OnlyMinus,
        cents: ECurrencyFormatCents.IntegerCents,
      }),

    // example positive: +1000.67   negative: -1000.67
    plusMinus: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.PlusMinus,
        cents: ECurrencyFormatCents.IntegerCents,
      }),

    // example positive: 1000.67 | negative: 1000.67
    omitSign: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.OmitSign,
        cents: ECurrencyFormatCents.IntegerCents,
      }),
  },
  onlyInteger: {
    // example positive: 1000   negative: -1000
    onlyMinus: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.OnlyMinus,
        cents: ECurrencyFormatCents.OnlyInteger,
      }),

    // example positive: +1000   negative: -1000
    plusMinus: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.PlusMinus,
        cents: ECurrencyFormatCents.OnlyInteger,
      }),

    // example positive: 1000   negative: 1000
    omitSign: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.OmitSign,
        cents: ECurrencyFormatCents.OnlyInteger,
      }),
  },
  onlyCents: {
    // example positive: .67 | negative: .67
    omitSign: (amount: number) =>
      formatCurrency({
        amount,
        sign: ECurrencyFormatSign.OmitSign,
        cents: ECurrencyFormatCents.OnlyCents,
      }),
  },
}

// Adds cents to currency if they are missing
export function addCentsToCurrency(value: string | undefined): string {
  if (!value) return ''

  if (!value.includes('.')) {
    return `${value}.00`
  }

  if (value.split('.')[1].length === 1) {
    return `${value}0`
  }

  if (!value.split('.')[1]) {
    return `${value}00`
  }

  return value
}

// Methods formats free text input into currency format
// Removing all non-digit characters except for the first decimal point
// Attention: this method works only for USD currency
export function formatInputToCurrency(input: string, addCurrencySymbol?: boolean): string {
  if (!input) return ''

  // removing everything which is not a digit or a decimal point
  let result = input.replace(/[^0-9.]/g, '')
  // remove all decimal points after the first one
  if (result.split('.').length > 2) {
    result = result.replace(/\.+$/, '')
  }

  // format the integer part of the number to currency
  if (result.split('.').length > 1) {
    const formattedIntegers = Currency.onlyInteger.omitSign(Number(result.split('.')[0]))
    // Slice any numbers from cents when chars are > 2
    const formattedCents: string = result.split('.')[1]?.slice(0, 2)

    result = `${formattedIntegers}.${formattedCents}`
  } else {
    result = Currency.onlyInteger.omitSign(Number(result))
  }

  return addCurrencySymbol ? `${getCurrencySymbol(DEFAULT_CURRENCY_CODE)}${result}` : result
}

export function appendDecimal(numberString: string) {
  if (numberString.includes('.')) {
    return numberString
  }
  return `${numberString}.00`
}

// converts currency display format to number
export function convertCurrencyToNumber(currencyString: string) {
  // Remove commas and convert to a number
  const number = parseFloat(currencyString.replace(/,/g, ''))
  return isNaN(number) ? null : number
}

export interface TConvertToPercentage {
  t: UtilsTranslationFunction
  rate: number
  /**  Number of digits after the decimal point. Must be in the range 0 - 20, inclusive. */
  fractionDigits?: number
}

export const convertToPercentage = ({ t, rate, fractionDigits = 2 }: TConvertToPercentage): string => {
  let percentage = (rate * 100).toFixed(fractionDigits).replace(/0+$/, '')

  // If percentage was stripped away of '00'
  // and last character is now decimal, trim that too
  if (percentage.endsWith('.')) {
    percentage = percentage.substring(0, percentage.length - 1)
  }

  return t(CURRENCY_TRANSLATION_KEYS.GENERAL.PERCENT_FORMATTER, { percentage })
}

/**
 * Converts a rate to percentage string using trn function
 * @param t - Translation function
 * @param rate - The rate to convert (e.g., 0.15 for 15%)
 * @param fractionDigits - Number of digits after decimal point (0-20)
 * @returns Formatted percentage string (e.g., "15%")
 */
export const convertToPercentageWithTranslation = (
  t: UtilsTranslationFunction,
  rate: number,
  fractionDigits = 2,
): string => {
  return convertToPercentage({ t, rate, fractionDigits })
}

export const getFormattedBalanceForOverview = (b?: Money) => ({
  integers: b?.amount ? Currency.onlyInteger.onlyMinus(b.amount) : '0',
  cents: b?.amount ? Currency.onlyCents.omitSign(b.amount) : '.00',
})

export const buildBasicCurrency = ({ currencyCode = 'USD', amount = 0 }: { currencyCode?: string; amount: number }) => {
  return `${getCurrencySymbol(currencyCode)}${Currency.integerCents.omitSign(amount)}`
}
